/**
 * One nav definition, consumed by the sidebar, the command palette and the
 * breadcrumb trail.
 *
 * It used to live inside `app-sidebar.tsx`, which meant the palette and the
 * header had no idea what the modules were called. Anything that needs to name
 * a route reads this.
 */
import {
  Boxes,
  CalendarCheck2,
  ClipboardCheck,
  CreditCard,
  FileSignature,
  FileBarChart,
  Heart,
  Images,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  MapPin,
  Package,
  Percent,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Star,
  Store,
  Trash2,
  Users,
} from 'lucide-react';
import type { AdminStats } from '@/features/stats/hooks';

export type NavItem = {
  title: string;
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Not yet implemented — rendered disabled with a lock, not navigable. */
  locked?: boolean;
  /** Hidden entirely from anyone who isn't a super admin. */
  super_admin_only?: boolean;
  /**
   * Kept in the codebase but taken out of the sidebar and the palette.
   *
   * For a module that is wired up and working but has nothing to show yet — the
   * route still resolves if you type it, so nothing is lost and it can come back
   * by deleting one line.
   */
  hidden?: boolean;
  /**
   * Which dashboard queue count to show as a badge on this item. The sidebar
   * is where an admin looks first, so the counts that mean "something is
   * waiting" belong there rather than only on the dashboard.
   */
  badge?: keyof AdminStats['queues'];
  /** Extra words the command palette should match on. */
  keywords?: string;
  /** One line for the page header, so every module says what it is for. */
  blurb?: string;
};

export type NavGroup = { label: string; items: NavItem[] };

export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      {
        title: 'Dashboard',
        to: '/',
        icon: LayoutDashboard,
        keywords: 'home overview stats revenue',
        blurb: 'Queues, revenue and recent activity',
      },
    ],
  },
  {
    label: 'Marketplace',
    items: [
      {
        title: 'Users',
        to: '/users',
        icon: Users,
        keywords: 'customers accounts owners people',
        blurb: 'Every account on the platform',
      },
      {
        title: 'Vendors',
        to: '/vendors',
        icon: Store,
        badge: 'vendors_awaiting_kyc',
        keywords: 'businesses partners kyc onboarding',
        blurb: 'Businesses, KYC and service catalogues',
      },
      {
        title: 'Agreements',
        to: '/agreements',
        icon: FileSignature,
        keywords: 'vendor agreement terms contract pdf accept acceptance legal versions',
        blurb: 'Per-module vendor agreements and who has accepted them',
      },
      {
        title: 'Adoption',
        to: '/adoption',
        icon: Heart,
        keywords: 'rehoming listings shelter',
        blurb: 'Adoption listings and applications',
      },
    ],
  },
  {
    label: 'Transactions',
    items: [
      {
        title: 'Bookings',
        to: '/bookings',
        icon: CalendarCheck2,
        badge: 'bookings_awaiting_vendor',
        keywords: 'appointments slots services grooming boarding',
        blurb: 'Service appointments across every module',
      },
      {
        title: 'Support requests',
        to: '/support-requests',
        icon: LifeBuoy,
        badge: 'support_open',
        keywords: 'tickets help escalations complaints',
        blurb: 'Open tickets and escalations',
      },
      /*
       * Café and restaurant orders off a menu — `Order` → `OrderItem` →
       * `MenuItem`, self-managed and cash on delivery.
       *
       * Hidden, not deleted. The module is wired end to end but the table is
       * empty because the menu catalogue is barely seeded, and two nav entries
       * both called some flavour of "orders" was the single most confusing thing
       * in this sidebar. Delete the `hidden` line to bring it back.
       */
      {
        title: 'Food orders',
        to: '/orders',
        icon: ShoppingBag,
        hidden: true,
        keywords: 'menu cafe restaurant dining food',
        blurb: 'Café and restaurant orders placed off a menu',
      },
      {
        // The shop: physical goods, prepaid, delivered. One customer order fans
        // out into a shipment per vendor.
        title: 'Orders',
        to: '/commerce-orders',
        icon: Package,
        badge: 'orders_pending_payment',
        keywords: 'shop products retail fulfilment pharmacy prescriptions shipments commerce',
        blurb: 'Shop orders for products and medicines, and their shipments',
      },
      {
        title: 'Payments',
        to: '/payments',
        icon: CreditCard,
        keywords: 'razorpay refunds captures ledger money',
        blurb: 'Captures, refunds and gateway records',
      },
      {
        /*
         * Filed under Transactions rather than Settings: approving one deactivates a live
         * account and strands whatever it still has in flight, so it belongs beside the
         * bookings and orders that decision is made against.
         */
        title: 'Account deletions',
        to: '/account-deletions',
        icon: Trash2,
        badge: 'account_deletions_pending',
        keywords: 'delete close account gdpr privacy erase removal offboarding',
        blurb: 'People asking to have their account closed',
      },
    ],
  },
  {
    /*
     * The three screens that decide what is on sale and what it costs.
     *
     * They were scattered: approvals sat under Transactions (it is not a transaction —
     * no money and no customer), while the catalogue and the fee rules were filed under
     * Settings beside the admin accounts. But they are one chain read at three depths.
     * An approval shows the fee a shopper will pay; that fee comes from the listing, or
     * its catalogue master, or its category rule; and the only way to change it is on one
     * of the other two screens. Grouping them is how you find the second screen while
     * still looking at the first.
     */
    label: 'Catalogue',
    items: [
      {
        title: 'Product approvals',
        to: '/product-approvals',
        icon: ClipboardCheck,
        keywords: 'moderation listings review queue submissions pending edits',
        blurb: 'Vendor submissions waiting on a decision',
      },
      {
        title: 'Master catalogue',
        to: '/catalogue',
        icon: Boxes,
        keywords: 'master products skus curated shared listings',
        blurb: 'The curated products every vendor lists from',
      },
      {
        title: 'Categories',
        to: '/product-categories',
        icon: Layers,
        keywords: 'category categories taxonomy shop sections merchandising',
        blurb: 'What every product is filed under',
      },
      {
        title: 'App categories',
        to: '/modules',
        icon: LayoutGrid,
        keywords: 'module modules rail rails icons home health services tabs artwork order',
        blurb: 'The rails customers tap on the app tabs',
      },
      {
        title: 'Platform fees',
        to: '/platform-fees',
        icon: Percent,
        keywords: 'commission take rate pricing cut margin',
        blurb: 'What the platform charges, per category and per module',
      },
    ],
  },
  {
    label: 'Insights',
    items: [
      {
        title: 'Reviews',
        to: '/reviews',
        icon: Star,
        badge: 'reviews_reported',
        keywords: 'ratings feedback moderation reported',
        blurb: 'Ratings and reported content',
      },
      {
        title: 'Banners',
        to: '/banners',
        icon: Images,
        keywords: 'promotions carousel marketing app home',
        blurb: 'In-app promotional placements',
      },
      {
        title: 'Reports',
        to: '/reports',
        icon: FileBarChart,
        keywords: 'analytics revenue exports gmv',
        blurb: 'Revenue and settlement reporting',
      },
    ],
  },
  {
    label: 'Settings',
    items: [
      {
        title: 'App config',
        to: '/app-config',
        icon: Smartphone,
        keywords: 'force update version build maintenance kill switch splash store release blocked',
        blurb: 'Force update, maintenance mode and support details for the mobile apps',
      },
      {
        title: 'Service areas',
        to: '/service-regions',
        icon: MapPin,
        keywords: 'regions launch city coverage serviceable pincode area rollout',
        blurb: 'Where the app is open for business',
      },
      {
        title: 'Admins',
        to: '/admins',
        icon: ShieldCheck,
        super_admin_only: true,
        keywords: 'operators staff roles access',
        blurb: 'Console operator accounts',
      },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.label })));

/**
 * The nav entry that owns a path, for breadcrumbs and page titles.
 *
 * Longest matching prefix wins so `/vendors/abc123` resolves to Vendors while
 * `/` only ever matches the dashboard.
 */
export function nav_item_for_path(pathname: string): (NavItem & { group: string }) | undefined {
  if (pathname === '/') return ALL_ITEMS.find((i) => i.to === '/');
  return ALL_ITEMS.filter((i) => i.to !== '/' && pathname.startsWith(i.to)).sort(
    (a, b) => b.to.length - a.to.length,
  )[0];
}
